import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Fetchexecutequery,
} from "../../../services/process/BRS1/brsService7";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import {
  Globe, Users, Building2, CalendarDays, FileText, Send, CheckCircle,
  TrendingUp, AlertTriangle, Shield, Activity, BarChart2,
  Download, Search, Filter, RefreshCw, ArrowUpRight, ArrowDownRight,
  Zap, Star, Clock, Award, Target, Layers,
  LayoutDashboard, Plus,
  GraduationCap, Calendar, DollarSign, UserCheck, Brain,
  Lightbulb, Rocket, MessageSquare, ClipboardList,
  PieChart as PieIcon, Bell, Settings, LogOut, Menu,
  TrendingDown, Minus, Eye, MoreHorizontal, Hash,
} from "lucide-react";

// ── Design Tokens ─────────────────────────────────────────────────────────────
const T = {
  navy:      "#0a2352",
  navyDark:  "#061535",
  navyMid:   "#143272",
  navyLight: "#1e4080",
  orange:    "#f97316",
  bg:        "#f0f4fa",
  bgAlt:     "#e8eef7",
  card:      "#ffffff",
  border:    "#d4dce8",
  borderLight: "#e8eef5",
  text:      "#0f172a",
  textMid:   "#334155",
  muted:     "#64748b",
  light:     "#e8eef5",
  success:   "#16a34a",
  danger:    "#dc2626",
  warn:      "#d97706",
  purple:    "#7c3aed",
  accent:    "#2563eb",
  cyan:      "#0891b2",
  teal:      "#0d9488",
  rose:      "#e11d48",
  indigo:    "#4338ca",
  sidebar:   "#0a1628",
  sidebarItem: "#0f1f3d",
};

// Inter from DynamicForm
const DONUT_COLORS = [T.accent, T.indigo, T.teal, T.orange, T.purple, T.cyan, T.success, T.rose];

// ── Helpers ───────────────────────────────────────────────────────────────────
const getUserCredentials = () => JSON.parse(sessionStorage.getItem("userCredentials") || "{}");
const getTenantId = (uc) => uc.tenants_id || uc.tenant_id || 0;
const MONTH_ORDER = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const REGION_BY_COUNTRY = {
  USA:"North America","United States":"North America",Canada:"North America",
  UK:"Europe","United Kingdom":"Europe",France:"Europe",Germany:"Europe",Switzerland:"Europe",Sweden:"Europe",
  India:"Asia Pacific",Singapore:"Asia Pacific",Australia:"Asia Pacific",
  Japan:"Asia Pacific",China:"Asia Pacific","S. Korea":"Asia Pacific","South Korea":"Asia Pacific",
  UAE:"Middle East","United Arab Emirates":"Middle East",
  "South Africa":"Africa",Brazil:"South America",
};
const COUNTRY_FLAG = {
  USA:"🇺🇸","United States":"🇺🇸",UK:"🇬🇧","United Kingdom":"🇬🇧",Canada:"🇨🇦",
  Germany:"🇩🇪",Australia:"🇦🇺",Singapore:"🇸🇬",India:"🇮🇳",France:"🇫🇷",
  Japan:"🇯🇵","S. Korea":"🇰🇷","South Korea":"🇰🇷",UAE:"🇦🇪","South Africa":"🇿🇦",
  Sweden:"🇸🇪",Brazil:"🇧🇷",China:"🇨🇳",Switzerland:"🇨🇭",
};
const getFlag = (country) => COUNTRY_FLAG[country] || "🌐";
const safeDate = (v) => { if (!v) return null; const d = new Date(v); return isNaN(d.getTime()) ? null : d; };
const monthKey = (v) => { const d = safeDate(v); return d ? `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}` : null; };
const monthSort = (v) => { const d = safeDate(v); return d ? d.getTime() : 0; };
const regionForCountry = (c) => REGION_BY_COUNTRY[c] || "Other";
const parseNumber = (v) => { const n = Number(v); return isFinite(n) ? n : 0; };
const pct = (part, total, digits=0) => total ? Number(((part/total)*100).toFixed(digits)) : 0;
const avg = (arr) => arr.length ? arr.reduce((s,v)=>s+v,0)/arr.length : 0;
const daysBetween = (from, to) => { const a=safeDate(from), b=safeDate(to); if(!a||!b) return null; return Math.max(0,(b-a)/(86400000)); };
const relativeTime = (v) => { const d=safeDate(v); if(!d) return "now"; const m=Math.max(1,Math.round((Date.now()-d.getTime())/60000)); if(m<60) return `${m}m`; const h=Math.round(m/60); if(h<24) return `${h}h`; return `${Math.round(h/24)}d`; };
const formatMonth = (v) => { const d=safeDate(v); if(!d) return null; return `${MONTH_ORDER[d.getMonth()]} '${String(d.getFullYear()).slice(-2)}`; };
const monthBucket = (v) => monthKey(v) || "unknown";
const makeSpark = (series, fallback=0) => { const vals=(series&&series.length?series:[fallback]).map(parseNumber); return vals.length?vals:[fallback]; };
const fmtDate = (v) => { const d=safeDate(v); if(!d) return "—"; return d.toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"}); };
const fmt = (n) => n >= 1000 ? `${(n/1000).toFixed(1)}k` : String(n);

// ── Global mutable data stores ────────────────────────────────────────────────
let GLOBAL_DASHBOARD_DATA = { metrics:{schools:0,universities:0,events:0,applications:0,shortlisted:0,invitations:0,accepted:0,conversion:0} };
let KPI_CARDS=[], GROWTH_DATA=[], FUNNEL_DATA=[], REGION_DATA=[], TOP_COUNTRIES=[];
let HEALTH_CARDS=[], MARKETPLACE_SCORES=[], TOP_SCHOOLS=[], TOP_UNIVERSITIES=[];
let EVENT_PERFORMANCE=[], REVENUE_DATA=[], REVENUE_BY_REGION=[];
let AI_METRICS=[], PIE_APPS_REGION=[], PIE_INVITES_REGION=[], PIE_ACCEPTED_REGION=[], PIE_EVENTS_CATEGORY=[];
let KANBAN_STAGES=[], RISK_DATA=[], SLA_METRICS=[], INSIGHTS=[], UPCOMING=[], NOTIFICATIONS=[], ACTIVITY_FEED=[];

// ── Core data builder (unchanged logic) ──────────────────────────────────────
const buildGlobalDashboardData = ({ schools=[], universities=[], events=[], applications=[], invitations=[] }) => {
  const schoolRows = schools.map((r) => ({ ...r, type:"school", name:r.school_name||"Unknown School", country:r.country||"Unknown", completion:parseNumber(r.completion_percentage), status:r.profile_status||"draft", created_date:r.created_date||r.modified_date||null, modified_date:r.modified_date||r.created_date||null, entity_key:String(r.entity_id||"") }));
  const universityRows = universities.map((r) => ({ ...r, type:"university", name:r.university_name||"Unknown University", country:r.country||"Unknown", completion:parseNumber(r.completion_percentage), status:r.profile_status||"draft", created_date:r.created_date||r.modified_date||null, modified_date:r.modified_date||r.created_date||null, entity_key:String(r.entity_id||"") }));
  const profileByEntity = new Map();
  [...schoolRows,...universityRows].forEach((r) => { if(r.entity_key&&r.entity_key!=="0") profileByEntity.set(r.entity_key,r); });
  const eventRows = events.map((r) => ({ ...r, country:r.country||"Unknown", city:r.city||"", category:r.event_category||"General", status:r.event_status||"draft", created_date:r.created_date||r.modified_date||null, modified_date:r.modified_date||r.created_date||null }));
  const applicationRows = applications.map((r) => { const owner=profileByEntity.get(String(r.applicant_entity_id||""))||{}; return { ...r, country:r.country||owner.country||"Unknown", city:r.city||owner.city||"", school_name:r.school_name||owner.name||"Unknown", owner_type:owner.type||"unknown", applied_on:r.applied_on||r.created_date||null, reviewed_on:r.reviewed_on||null, invited_on:r.invited_on||null, response_deadline:r.response_deadline||null, responded_on:r.responded_on||null, modified_date:r.modified_date||r.applied_on||r.created_date||null, application_status:r.application_status||"applied", invitation_status:r.invitation_status||null }; });
  const invitationRows = invitations.map((r) => { const owner=profileByEntity.get(String(r.applicant_entity_id||""))||{}; return { ...r, country:r.country||owner.country||"Unknown", city:r.city||owner.city||"", school_name:r.school_name||owner.name||"Unknown", owner_type:owner.type||"unknown", invited_on:r.invited_on||r.created_date||r.modified_date||null, responded_on:r.responded_on||null, acceptance_deadline:r.acceptance_deadline||null, invitation_status:r.invitation_status||"pending", modified_date:r.modified_date||r.invited_on||r.created_date||null }; });

  const acceptedInvitations = invitationRows.filter((r)=>r.invitation_status==="accepted");
  const shortlistedApplications = applicationRows.filter((r)=>["shortlisted","invited","accepted"].includes(r.application_status));
  const activeEvents = eventRows.filter((r)=>!["archived","draft"].includes(r.status));
  const schoolsTotal=schoolRows.length, universitiesTotal=universityRows.length, eventsTotal=activeEvents.length, applicationsTotal=applicationRows.length, shortlistedTotal=shortlistedApplications.length, invitationsTotal=invitationRows.length, acceptedTotal=acceptedInvitations.length, conversionTotal=pct(acceptedTotal,invitationsTotal,0), totalProfiles=schoolsTotal+universitiesTotal;

  const allMonthKeys = Array.from(new Set([...schoolRows.map((r)=>monthBucket(r.created_date)),...universityRows.map((r)=>monthBucket(r.created_date)),...eventRows.map((r)=>monthBucket(r.created_date)),...applicationRows.map((r)=>monthBucket(r.applied_on)),...invitationRows.map((r)=>monthBucket(r.invited_on))])).filter((k)=>k!=="unknown").sort();
  const growthData = allMonthKeys.length ? allMonthKeys.map((key)=>({ key, month:formatMonth(`${key}-01`)||key, Schools:schoolRows.filter((r)=>monthBucket(r.created_date)===key).length, Universities:universityRows.filter((r)=>monthBucket(r.created_date)===key).length, Events:eventRows.filter((r)=>monthBucket(r.created_date)===key).length, Applications:applicationRows.filter((r)=>monthBucket(r.applied_on)===key).length, Invitations:invitationRows.filter((r)=>monthBucket(r.invited_on)===key).length, Accepted:acceptedInvitations.filter((r)=>monthBucket(r.invited_on)===key).length })) : [{ key:"live", month:"Live", Schools:schoolsTotal, Universities:universitiesTotal, Events:eventsTotal, Applications:applicationsTotal, Invitations:invitationsTotal, Accepted:acceptedTotal }];
  const lastKey = allMonthKeys[allMonthKeys.length-1];

  const regionBuckets={};
  const addRegion=(country,payload)=>{ const region=regionForCountry(country); regionBuckets[region]=regionBuckets[region]||{region,schools:0,univs:0,events:0,apps:0,invitations:0,accepted:0}; Object.keys(payload).forEach((k)=>{regionBuckets[region][k]+=parseNumber(payload[k]);}); };
  schoolRows.forEach((r)=>addRegion(r.country,{schools:1}));
  universityRows.forEach((r)=>addRegion(r.country,{univs:1}));
  eventRows.forEach((r)=>addRegion(r.country,{events:1}));
  applicationRows.forEach((r)=>addRegion(r.country,{apps:1}));
  invitationRows.forEach((r)=>addRegion(r.country,{invitations:1,accepted:r.invitation_status==="accepted"?1:0}));
  const regionData = Object.values(regionBuckets).sort((a,b)=>b.apps-a.apps||b.events-a.events);

  const countryBuckets={};
  [...schoolRows,...universityRows,...applicationRows,...invitationRows].forEach((r)=>{ const c=r.country||"Unknown"; countryBuckets[c]=countryBuckets[c]||{country:c,flag:getFlag(c),schools:0,univs:0,applications:0,invitations:0,accepted:0}; if(r.type==="school") countryBuckets[c].schools+=1; if(r.type==="university") countryBuckets[c].univs+=1; if("applied_on"in r) countryBuckets[c].applications+=1; if("invited_on"in r&&!("applied_on"in r)) countryBuckets[c].invitations+=1; if(r.invitation_status==="accepted") countryBuckets[c].accepted+=1; });
  const topCountries = Object.values(countryBuckets).map((r)=>({...r,activity:r.schools+r.univs+r.applications+r.invitations,growth:`+${pct(r.accepted+r.applications,Math.max(r.applications+r.invitations,1),0)}%`,share:`${pct(r.applications+r.invitations,Math.max(applicationsTotal+invitationsTotal,1),0)}%`})).sort((a,b)=>b.activity-a.activity).slice(0,10);

  const funnelData = [
    {name:"Events Published",value:activeEvents.length,fill:T.accent},
    {name:"Applications Submitted",value:applicationsTotal,fill:T.indigo},
    {name:"Shortlisted",value:shortlistedTotal,fill:T.purple},
    {name:"Invitations Sent",value:invitationsTotal,fill:T.cyan},
    {name:"Invitations Accepted",value:acceptedTotal,fill:T.teal},
    {name:"Events Completed",value:eventRows.filter((r)=>r.status==="completed").length,fill:T.success},
  ];

  const profilePending=[...schoolRows,...universityRows].filter((r)=>!["published","approved","active","complete"].includes(String(r.status||"").toLowerCase())||r.completion<100).length;
  const eventsPending=eventRows.filter((r)=>["draft","under_review"].includes(String(r.status||"").toLowerCase())).length;
  const backlogApps=applicationRows.filter((r)=>["applied","pending","under_review"].includes(String(r.application_status||"").toLowerCase())).length;
  const overdueInvites=invitationRows.filter((r)=>{const dl=safeDate(r.acceptance_deadline);return r.invitation_status==="pending"&&dl&&dl<new Date();}).length;
  const rejectedCases=applicationRows.filter((r)=>["rejected","declined","withdrawn"].includes(String(r.application_status||"").toLowerCase())).length+invitationRows.filter((r)=>["rejected","declined","expired"].includes(String(r.invitation_status||"").toLowerCase())).length;
  const inactiveProfiles=[...schoolRows,...universityRows].filter((r)=>{const m=safeDate(r.modified_date||r.created_date);return m&&(Date.now()-m.getTime())/86400000>90;}).length;

  const healthCards=[
    {label:"Profiles Pending",value:profilePending,sla:"Live",risk:profilePending>20?"high":"medium",icon:Shield,color:T.success},
    {label:"Events Pending",value:eventsPending,sla:"Live",risk:eventsPending>10?"high":"medium",icon:CalendarDays,color:T.warn},
    {label:"App Backlog",value:backlogApps,sla:"Live",risk:backlogApps>25?"high":"medium",icon:ClipboardList,color:T.danger},
    {label:"Overdue Invites",value:overdueInvites,sla:"Live",risk:overdueInvites>10?"critical":"high",icon:MessageSquare,color:T.rose},
    {label:"Rejected",value:rejectedCases,sla:"Live",risk:rejectedCases>25?"medium":"low",icon:AlertTriangle,color:T.accent},
    {label:"Inactive Profiles",value:inactiveProfiles,sla:"Live",risk:inactiveProfiles>50?"medium":"low",icon:ClipboardList,color:T.purple},
  ];

  const completionRate=avg([...schoolRows,...universityRows].map((r)=>r.completion));
  const eventPublishRate=pct(activeEvents.length,Math.max(eventRows.length,1),1);
  const shortlistRate=pct(shortlistedTotal,Math.max(applicationsTotal,1),1);
  const inviteRate=pct(invitationsTotal,Math.max(applicationsTotal,1),1);
  const acceptanceRate=pct(acceptedTotal,Math.max(invitationsTotal,1),1);
  const engagementScore=pct(shortlistedTotal+acceptedTotal,Math.max(applicationsTotal+invitationsTotal,1),1);

  const marketplaceScores=[
    {name:"School Completion",value:Math.round(avg(schoolRows.map((r)=>r.completion))),color:T.accent},
    {name:"University Completion",value:Math.round(avg(universityRows.map((r)=>r.completion))),color:T.teal},
    {name:"Event Publish Rate",value:Math.round(eventPublishRate),color:T.orange},
    {name:"Shortlist Rate",value:Math.round(shortlistRate),color:T.success},
    {name:"Invite Acceptance",value:Math.round(acceptanceRate),color:T.purple},
    {name:"Country Coverage",value:Math.round(pct(Object.keys(countryBuckets).length,Math.max(totalProfiles,1),1)),color:T.cyan},
    {name:"Engagement Score",value:Math.round(engagementScore),color:T.indigo},
    {name:"Backlog Health",value:Math.max(0,100-Math.round(pct(overdueInvites+backlogApps,Math.max(applicationsTotal+invitationsTotal,1),0))),color:T.rose},
  ];

  const schoolLeaderboard=[...schoolRows].map((row)=>{ const ek=row.entity_key; const rowApps=applicationRows.filter((a)=>String(a.applicant_entity_id||"")===ek); const rowInvites=invitationRows.filter((i)=>String(i.applicant_entity_id||"")===ek); const acc=rowInvites.filter((i)=>i.invitation_status==="accepted").length; return {rank:0,school:row.name,country:row.country,flag:getFlag(row.country),events:eventRows.filter((e)=>String(e.creator_entity_id||"")===ek).length,apps:rowApps.length,invites:rowInvites.length,acceptance:`${Math.round(pct(acc,Math.max(rowInvites.length,1),0))}%`,score:Math.round((row.completion+pct(acc,Math.max(rowInvites.length,1),0)+pct(rowApps.length,Math.max(applicationsTotal,1),0))/3)}; }).sort((a,b)=>b.apps-a.apps||b.score-a.score).slice(0,20).map((r,i)=>({...r,rank:i+1}));

  const universityLeaderboard=[...universityRows].map((row)=>{ const ek=row.entity_key; const rowApps=applicationRows.filter((a)=>String(a.applicant_entity_id||"")===ek); const rowInvites=invitationRows.filter((i)=>String(i.applicant_entity_id||"")===ek); const acc=rowInvites.filter((i)=>i.invitation_status==="accepted").length; return {rank:0,university:row.name,country:row.country,flag:getFlag(row.country),apps:rowApps.length,invites:rowInvites.length,attended:acc,success:`${Math.round(pct(acc,Math.max(rowInvites.length,1),0))}%`,engagement:Math.round((row.completion+pct(rowApps.length,Math.max(applicationsTotal,1),0)+pct(acc,Math.max(invitationsTotal,1),0))/3)}; }).sort((a,b)=>b.apps-a.apps||b.engagement-a.engagement).slice(0,20).map((r,i)=>({...r,rank:i+1}));

  const eventPerformance=eventRows.map((ev)=>({event:ev.event_name,country:ev.country,city:ev.city||ev.country,apps:applicationRows.filter((a)=>String(a.event_id||"")===String(ev.primary_id||"")).length,shortlisted:applicationRows.filter((a)=>String(a.event_id||"")===String(ev.primary_id||"")&&["shortlisted","invited","accepted"].includes(a.application_status)).length,invited:invitationRows.filter((i)=>String(i.event_id||"")===String(ev.primary_id||"")).length,accepted:invitationRows.filter((i)=>String(i.event_id||"")===String(ev.primary_id||"")&&i.invitation_status==="accepted").length,attendance:invitationRows.filter((i)=>String(i.event_id||"")===String(ev.primary_id||"")&&i.invitation_status==="accepted").length,status:ev.status||"draft"})).sort((a,b)=>b.apps-a.apps||b.invited-a.invited);

  const catBuckets={};
  eventRows.forEach((e)=>{const c=e.category||"General";catBuckets[c]=(catBuckets[c]||0)+1;});
  const eventCategories=Object.entries(catBuckets).map(([n,v],i)=>({name:n,value:v,fill:DONUT_COLORS[i%DONUT_COLORS.length]})).sort((a,b)=>b.value-a.value);
  const pieAppsRegion=regionData.map((r,i)=>({name:r.region,value:r.apps,fill:DONUT_COLORS[i%DONUT_COLORS.length]}));
  const pieInvitesRegion=regionData.map((r,i)=>({name:r.region,value:r.invitations,fill:DONUT_COLORS[(i+1)%DONUT_COLORS.length]}));
  const pieAcceptedRegion=regionData.map((r,i)=>({name:r.region,value:r.accepted,fill:DONUT_COLORS[(i+2)%DONUT_COLORS.length]}));
  const revenueData=growthData.map((r)=>({month:r.month,schools:r.Schools,universities:r.Universities,events:r.Events,applications:r.Applications}));
  const revenueByRegion=regionData.map((r,i)=>({name:r.region,value:r.apps+r.invitations+r.events,fill:DONUT_COLORS[i%DONUT_COLORS.length]}));
  const aiMetrics=[{label:"Suggested Matches",value:shortlistedTotal.toLocaleString(),sub:"Live shortlisted",icon:Brain,color:T.purple,trend:`${shortlistRate.toFixed(1)}%`},{label:"Successful Matches",value:acceptedTotal.toLocaleString(),sub:"Accepted invitations",icon:CheckCircle,color:T.success,trend:`${acceptanceRate.toFixed(1)}%`},{label:"Match Accuracy",value:`${acceptanceRate.toFixed(1)}%`,sub:"Invitation acceptance",icon:Target,color:T.accent,trend:`${inviteRate.toFixed(1)}%`},{label:"Recommendation CTR",value:`${inviteRate.toFixed(1)}%`,sub:"Invitations per application",icon:Zap,color:T.orange,trend:`${shortlistRate.toFixed(1)}%`},{label:"Profile Completeness",value:`${Math.round(completionRate)}%`,sub:"Avg across profiles",icon:UserCheck,color:T.teal,trend:`${Math.round(completionRate)}%`},{label:"Engagement Score",value:`${engagementScore.toFixed(1)}/10`,sub:"Derived from activity",icon:Activity,color:T.indigo,trend:`${engagementScore.toFixed(1)}`}];
  const kpiCards=[
    {icon:Building2,label:"Total Schools",value:schoolsTotal.toLocaleString(),rawValue:schoolsTotal,trend:`+${pct(schoolRows.filter((r)=>monthBucket(r.created_date)===lastKey).length,Math.max(schoolsTotal,1),0)}%`,sparkData:makeSpark(growthData.map((r)=>r.Schools),schoolsTotal),color:T.accent,positive:true},
    {icon:GraduationCap,label:"Universities",value:universitiesTotal.toLocaleString(),rawValue:universitiesTotal,trend:`+${pct(universityRows.filter((r)=>monthBucket(r.created_date)===lastKey).length,Math.max(universitiesTotal,1),0)}%`,sparkData:makeSpark(growthData.map((r)=>r.Universities),universitiesTotal),color:T.teal,positive:true},
    {icon:CalendarDays,label:"Active Events",value:eventsTotal.toLocaleString(),rawValue:eventsTotal,trend:`+${pct(eventRows.filter((r)=>monthBucket(r.created_date)===lastKey).length,Math.max(eventsTotal,1),0)}%`,sparkData:makeSpark(growthData.map((r)=>r.Events),eventsTotal),color:T.orange,positive:true},
    {icon:FileText,label:"Applications",value:applicationsTotal.toLocaleString(),rawValue:applicationsTotal,trend:`+${pct(applicationRows.filter((r)=>monthBucket(r.applied_on)===lastKey).length,Math.max(applicationsTotal,1),0)}%`,sparkData:makeSpark(growthData.map((r)=>r.Applications),applicationsTotal),color:T.purple,positive:true},
    {icon:Users,label:"Shortlisted",value:shortlistedTotal.toLocaleString(),rawValue:shortlistedTotal,trend:`+${pct(shortlistedTotal,Math.max(applicationsTotal,1),0)}%`,sparkData:makeSpark(growthData.map((r)=>r.Applications),shortlistedTotal),color:T.indigo,positive:true},
    {icon:Send,label:"Invitations Sent",value:invitationsTotal.toLocaleString(),rawValue:invitationsTotal,trend:`+${pct(invitationsTotal,Math.max(applicationsTotal,1),0)}%`,sparkData:makeSpark(growthData.map((r)=>r.Invitations),invitationsTotal),color:T.cyan,positive:true},
    {icon:CheckCircle,label:"Accepted",value:acceptedTotal.toLocaleString(),rawValue:acceptedTotal,trend:`+${pct(acceptedTotal,Math.max(invitationsTotal,1),0)}%`,sparkData:makeSpark(growthData.map((r)=>r.Accepted),acceptedTotal),color:T.success,positive:true},
    {icon:TrendingUp,label:"Conversion Rate",value:`${conversionTotal}%`,rawValue:conversionTotal,trend:`+${Math.round(conversionTotal/10)}%`,sparkData:makeSpark(growthData.map((r)=>r.Accepted),conversionTotal),color:T.rose,positive:true},
  ];

  const riskData=[
    {label:"Profiles Pending Review",value:profilePending,severity:profilePending>20?"high":"medium",color:T.warn},
    {label:"Events Pending Review",value:eventsPending,severity:eventsPending>10?"high":"medium",color:T.danger},
    {label:"Application Backlog",value:backlogApps,severity:backlogApps>25?"critical":"high",color:T.rose},
    {label:"Overdue Invitations",value:overdueInvites,severity:overdueInvites>10?"critical":"high",color:T.warn},
    {label:"Rejected / Declined",value:rejectedCases,severity:rejectedCases>25?"medium":"low",color:T.danger},
    {label:"Inactive Profiles",value:inactiveProfiles,severity:inactiveProfiles>50?"medium":"low",color:T.muted},
  ];
  const slaMetrics=[
    {label:"Avg School Completion",value:Math.round(avg(schoolRows.map((r)=>r.completion))||0),unit:"%",target:90,color:T.success},
    {label:"Avg University Completion",value:Math.round(avg(universityRows.map((r)=>r.completion))||0),unit:"%",target:90,color:T.success},
    {label:"Avg Application Review",value:Number(avg(applicationRows.map((r)=>daysBetween(r.applied_on,r.reviewed_on)).filter((v)=>v!==null)).toFixed(1))||0,unit:"days",target:4,color:T.warn},
    {label:"Avg Invitation Response",value:Number(avg(invitationRows.map((r)=>daysBetween(r.invited_on,r.responded_on)).filter((v)=>v!==null)).toFixed(1))||0,unit:"days",target:5,color:T.warn},
    {label:"Avg Event Duration",value:Number(avg(eventRows.map((r)=>daysBetween(r.event_from_date,r.event_to_date)).filter((v)=>v!==null)).toFixed(1))||0,unit:"days",target:7,color:T.success},
  ];
  const insights=[
    {icon:TrendingUp,color:T.teal,title:`${regionData[0]?.region||"Global"} leads activity`,body:`${regionData[0]?.apps||0} applications and ${regionData[0]?.invitations||0} invitations in the busiest region.`},
    {icon:GraduationCap,color:T.accent,title:"Profile completion rising",body:`Average completeness is ${Math.round(completionRate)}% across ${totalProfiles.toLocaleString()} active profiles.`},
    {icon:Star,color:T.orange,title:"Shortlists convert to invites",body:`${shortlistedTotal.toLocaleString()} shortlisted → ${invitationsTotal.toLocaleString()} invitations.`},
    {icon:Award,color:T.success,title:"Acceptance is measurable",body:`${acceptedTotal.toLocaleString()} invitations accepted — ${acceptanceRate.toFixed(1)}% acceptance rate.`},
    {icon:Zap,color:T.purple,title:"Backlog needs attention",body:`${backlogApps.toLocaleString()} applications and ${overdueInvites.toLocaleString()} overdue invitations pending.`},
    {icon:Globe,color:T.indigo,title:"Coverage across regions",body:`${Object.keys(countryBuckets).length.toLocaleString()} countries active across the live dataset.`},
  ];
  const upcoming=[
    ...eventRows.filter((r)=>r.event_from_date).map((r)=>({type:"event",label:`${r.event_name} starts`,date:fmtDate(r.event_from_date),color:T.accent,sort:monthSort(r.event_from_date)})),
    ...eventRows.filter((r)=>r.reg_close_date).map((r)=>({type:"deadline",label:`${r.event_name} reg. closes`,date:fmtDate(r.reg_close_date),color:T.warn,sort:monthSort(r.reg_close_date)})),
    ...invitationRows.filter((r)=>r.acceptance_deadline).map((r)=>({type:"invite",label:`${r.school_name} response due`,date:fmtDate(r.acceptance_deadline),color:T.purple,sort:monthSort(r.acceptance_deadline)})),
  ].sort((a,b)=>a.sort-b.sort).slice(0,8).map(({sort,...rest})=>rest);
  const notifications=[
    ...eventRows.slice(0,2).map((r)=>({text:`Event live: ${r.event_name}`,time:r.modified_date?`${relativeTime(r.modified_date)} ago`:"now",type:"info"})),
    ...applicationRows.slice(0,2).map((r)=>({text:`${r.school_name} application ${r.application_status}`,time:r.modified_date?`${relativeTime(r.modified_date)} ago`:"now",type:r.application_status==="rejected"?"danger":"warn"})),
    ...invitationRows.slice(0,2).map((r)=>({text:`${r.school_name} invitation ${r.invitation_status}`,time:r.modified_date?`${relativeTime(r.modified_date)} ago`:"now",type:r.invitation_status==="accepted"?"success":"warn"})),
  ].slice(0,7);
  const activityFeed=[...applicationRows.map((r)=>({action:"Application",detail:`${r.school_name}`,sub:r.event_name||"",time:r.modified_date||r.applied_on||null,status:r.application_status})),...invitationRows.map((r)=>({action:"Invitation",detail:`${r.school_name}`,sub:r.event_name||"",time:r.modified_date||r.invited_on||null,status:r.invitation_status})),...eventRows.map((r)=>({action:"Event",detail:r.event_name,sub:r.country||"",time:r.modified_date||r.created_date||null,status:r.status}))].filter((r)=>r.time).sort((a,b)=>monthSort(b.time)-monthSort(a.time)).slice(0,20).map((r)=>({...r,time:relativeTime(r.time)}));

  const kanbanMap={new:[],review:[],published:[],open:[],invited:[],completed:[]};
  eventRows.forEach((r)=>{ const card={title:r.event_name,apps:applicationRows.filter((a)=>String(a.event_id||"")===String(r.primary_id||"")).length}; const s=String(r.status||"").toLowerCase(); if(["draft","new"].includes(s)) kanbanMap.new.push(card); else if(s==="under_review") kanbanMap.review.push(card); else if(s==="published") kanbanMap.published.push(card); else if(s==="registration_open") kanbanMap.open.push(card); else if(s==="invitations_released") kanbanMap.invited.push(card); else if(["completed","registration_closed"].includes(s)) kanbanMap.completed.push(card); else kanbanMap.new.push(card); });
  const kanbanStages=[
    {key:"new",label:"New Events",color:T.muted,count:kanbanMap.new.length,cards:kanbanMap.new.slice(0,3)},
    {key:"review",label:"Under Review",color:T.warn,count:kanbanMap.review.length,cards:kanbanMap.review.slice(0,3)},
    {key:"published",label:"Published",color:T.accent,count:kanbanMap.published.length,cards:kanbanMap.published.slice(0,3)},
    {key:"open",label:"Apps Open",color:T.indigo,count:kanbanMap.open.length,cards:kanbanMap.open.slice(0,3)},
    {key:"invited",label:"Invites Sent",color:T.purple,count:kanbanMap.invited.length,cards:kanbanMap.invited.slice(0,3)},
    {key:"completed",label:"Completed",color:T.success,count:kanbanMap.completed.length,cards:kanbanMap.completed.slice(0,3)},
  ];

  return { metrics:{schools:schoolsTotal,universities:universitiesTotal,events:eventsTotal,applications:applicationsTotal,shortlisted:shortlistedTotal,invitations:invitationsTotal,accepted:acceptedTotal,conversion:conversionTotal}, growthData,funnelData,regionData,topCountries,healthCards,marketplaceScores, schoolLeaderboard,universityLeaderboard,eventPerformance, revenueData,revenueByRegion,aiMetrics, pieAppsRegion,pieInvitesRegion,pieAcceptedRegion,pieEventsCategory:eventCategories, kanbanStages,riskData,slaMetrics,insights,upcoming,notifications,activityFeed,kpiCards };
};

const applyGlobalDashboardData = (data) => {
  GLOBAL_DASHBOARD_DATA=data; KPI_CARDS=data.kpiCards; GROWTH_DATA=data.growthData; FUNNEL_DATA=data.funnelData;
  REGION_DATA=data.regionData; TOP_COUNTRIES=data.topCountries; HEALTH_CARDS=data.healthCards;
  MARKETPLACE_SCORES=data.marketplaceScores; TOP_SCHOOLS=data.schoolLeaderboard;
  TOP_UNIVERSITIES=data.universityLeaderboard; EVENT_PERFORMANCE=data.eventPerformance;
  REVENUE_DATA=data.revenueData; REVENUE_BY_REGION=data.revenueByRegion;
  AI_METRICS=data.aiMetrics; PIE_APPS_REGION=data.pieAppsRegion;
  PIE_INVITES_REGION=data.pieInvitesRegion; PIE_ACCEPTED_REGION=data.pieAcceptedRegion;
  PIE_EVENTS_CATEGORY=data.pieEventsCategory; KANBAN_STAGES=data.kanbanStages;
  RISK_DATA=data.riskData; SLA_METRICS=data.slaMetrics; INSIGHTS=data.insights;
  UPCOMING=data.upcoming; NOTIFICATIONS=data.notifications; ACTIVITY_FEED=data.activityFeed;
};

// ── Primitives ────────────────────────────────────────────────────────────────
const Panel = ({ children, style={}, className="" }) => (
  <div
    className={className}
    style={{
      background:T.card,
      borderRadius:10,
      border:`1px solid ${T.border}`,
      overflow:"hidden", ...style
    }}
  >
    {children}
  </div>
);
const PanelHeader = ({ title, subtitle, icon:Icon, right, compact=false }) => (
  <div style={{ padding:compact?"12px 16px":"14px 18px", borderBottom:`1px solid ${T.borderLight}`, display:"flex", alignItems:"center", justifyContent:"space-between", gap:10 }}>
    <div style={{ display:"flex", alignItems:"center", gap:9, minWidth:0 }}>
      {Icon && <div style={{ width:28, height:28, borderRadius:7, background:`${T.navy}12`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}><Icon size={14} color={T.navy}/></div>}
      <div style={{ minWidth:0 }}>
        <div style={{ fontSize:15, fontWeight:600, color:T.text, letterSpacing:-0.2, lineHeight:1.2 }}>{title}</div>
        {subtitle && <div style={{ fontSize:14, color:T.muted, marginTop:1, lineHeight:1.2, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{subtitle}</div>}
      </div>
    </div>
    {right && <div style={{ flexShrink:0 }}>{right}</div>}
  </div>
);

const Tag = ({ label, color, bg }) => (
  <span style={{ background:bg||color+"15", color, padding:"2px 8px", borderRadius:4, fontSize:13, fontWeight:600, letterSpacing:0.2, textTransform:"uppercase", whiteSpace:"nowrap", display:"inline-block" }}>{label}</span>
);

const Sparkline = ({ data, color, height=28, width=72 }) => {
  if(!data||!data.length) return null;
  const min=Math.min(...data), max=Math.max(...data), range=max-min||1;
  const pts=data.map((v,i)=>`${(i/(Math.max(data.length-1,1)))*width},${height-((v-min)/range)*height}`).join(" ");
  return (
    <svg width={width} height={height} style={{ display:"block", overflow:"visible" }}>
      <defs><linearGradient id={`sg-${color.replace("#","")}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity={0.15}/><stop offset="100%" stopColor={color} stopOpacity={0}/></linearGradient></defs>
      <polyline fill="none" stroke={color} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" points={pts}/>
    </svg>
  );
};

const ProgressBar = ({ value, max=100, color, height=5, bg=T.light }) => (
  <div style={{ height, background:bg, borderRadius:99, overflow:"hidden" }}>
    <div style={{ height:"100%", width:`${Math.min((value/Math.max(max,1))*100,100)}%`, background:color, borderRadius:99, transition:"width .5s" }}/>
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    completed:{bg:"#dcfce7",color:T.success}, active:{bg:"#dbeafe",color:T.accent}, published:{bg:"#fef3c7",color:T.warn}, draft:{bg:"#f1f5f9",color:T.muted}, under_review:{bg:"#ede9fe",color:T.purple}, registration_open:{bg:"#cffafe",color:T.cyan}, invitations_released:{bg:"#fce7f3",color:T.rose}, low:{bg:"#dcfce7",color:T.success}, medium:{bg:"#fef3c7",color:T.warn}, high:{bg:"#fee2e2",color:T.danger}, critical:{bg:"#ffe4e6",color:T.rose},
  };
  const s = map[status]||{bg:"#f1f5f9",color:T.muted};
  return <span style={{ background:s.bg, color:s.color, padding:"2px 8px", borderRadius:4, fontSize:13, fontWeight:600, letterSpacing:0.2, textTransform:"uppercase", display:"inline-block" }}>{status}</span>;
};

const Btn = ({ children, onClick, variant="ghost", size="sm", icon:Icon }) => {
  const styles = {
    primary: { bg:T.orange, color:"#fff", border:"none" },
    ghost: { bg:"transparent", color:T.muted, border:`1px solid ${T.border}` },
    navy: { bg:T.navy, color:"#fff", border:"none" },
  }[variant];
  return (
    <button onClick={onClick} style={{ display:"inline-flex", alignItems:"center", gap:5, padding:size==="sm"?"5px 12px":"7px 16px", borderRadius:7, background:styles.bg, color:styles.color, border:styles.border, fontSize:14, fontWeight:500, cursor:"pointer", letterSpacing:-0.1 }}>
      {Icon && <Icon size={12}/>}{children}
    </button>
  );
};

// ── KPI Strip ─────────────────────────────────────────────────────────────────
const KPIStrip = () => (
  <div style={{ display:"grid", gridTemplateColumns:"repeat(8,1fr)", gap:10, marginBottom:16 }}>
    {KPI_CARDS.map((k) => {
      const Icon = k.icon;
      const pos = k.positive !== false;
      return (
        <Panel key={k.label} style={{ padding:"14px 14px 12px" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
            <div style={{ width:30, height:30, borderRadius:8, background:k.color+"15", display:"flex", alignItems:"center", justifyContent:"center" }}>
              <Icon size={14} color={k.color}/>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:2 }}>
              {pos ? <ArrowUpRight size={9} color={T.success}/> : <ArrowDownRight size={9} color={T.danger}/>}
              <span style={{ fontSize:12, fontWeight:600, color:pos?T.success:T.danger }}>{k.trend}</span>
            </div>
          </div>
          <div style={{ fontSize:22, fontWeight:700, color:T.text, letterSpacing:-0.8, lineHeight:1, marginBottom:3 }}>{k.value}</div>
          <div style={{ fontSize:13, color:T.muted, fontWeight:500, marginBottom:8, lineHeight:1.2 }}>{k.label}</div>
          <Sparkline data={k.sparkData} color={k.color}/>
        </Panel>
      );
    })}
  </div>
);

// ── Overview Section ──────────────────────────────────────────────────────────
const OverviewSection = () => {
  const m = GLOBAL_DASHBOARD_DATA.metrics || {};
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
      <KPIStrip/>
      {/* Row 2: Growth chart + Funnel side by side */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 340px", gap:14 }}>
        <Panel>
          <PanelHeader title="Platform Growth" subtitle="Monthly trend across all entity types" icon={TrendingUp}/>
          <div style={{ padding:"16px 18px" }}>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={GROWTH_DATA} margin={{top:4,right:4,left:-20,bottom:0}}>
                <defs>
                  {[["s",T.accent],["u",T.teal],["e",T.orange],["a",T.purple]].map(([k,c])=>(
                    <linearGradient key={k} id={`ag-${k}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={c} stopOpacity={0.15}/>
                      <stop offset="95%" stopColor={c} stopOpacity={0}/>
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={T.borderLight}/>
                <XAxis dataKey="month" tick={{fontSize:12,fill:T.muted}} interval={2} axisLine={false} tickLine={false}/>
                <YAxis tick={{fontSize:12,fill:T.muted}} axisLine={false} tickLine={false}/>
                <Tooltip contentStyle={{borderRadius:8,border:`1px solid ${T.border}`,fontSize:14}} itemStyle={{fontSize:14}}/>
                <Legend wrapperStyle={{fontSize:13,paddingTop:8}}/>
                <Area type="monotone" dataKey="Schools"      stroke={T.accent} fill="url(#ag-s)" strokeWidth={1.5} dot={false}/>
                <Area type="monotone" dataKey="Universities" stroke={T.teal}   fill="url(#ag-u)" strokeWidth={1.5} dot={false}/>
                <Area type="monotone" dataKey="Events"       stroke={T.orange} fill="url(#ag-e)" strokeWidth={1.5} dot={false}/>
                <Area type="monotone" dataKey="Applications" stroke={T.purple} fill="url(#ag-a)" strokeWidth={1.5} dot={false}/>
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        {/* Funnel */}
        <Panel>
          <PanelHeader title="Conversion Funnel" subtitle="End-to-end pipeline" icon={Layers}/>
          <div style={{ padding:"14px 18px" }}>
            {FUNNEL_DATA.map((s,i)=>{
              const base = FUNNEL_DATA[0]?.value || 1;
              const w = Math.max(20,(s.value/base)*100);
              const conv = i===0?"100%":`${((s.value/base)*100).toFixed(1)}%`;
              return (
                <div key={s.name} style={{ marginBottom:10 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:3 }}>
                    <span style={{ fontSize:14, fontWeight:500, color:T.textMid }}>{s.name}</span>
                    <div style={{ display:"flex", gap:8 }}>
                      <span style={{ fontSize:14, fontWeight:700, color:s.fill }}>{s.value.toLocaleString()}</span>
                      <span style={{ fontSize:13, color:T.muted }}>{conv}</span>
                    </div>
                  </div>
                  <div style={{ height:6, background:T.light, borderRadius:99, overflow:"hidden" }}>
                    <div style={{ height:"100%", width:`${w}%`, background:s.fill, borderRadius:99 }}/>
                  </div>
                </div>
              );
            })}
            <div style={{ marginTop:14, padding:"12px", borderRadius:8, background:`${T.navy}`, border:`1px solid ${T.navy}` }}>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                {[
                  {l:"Shortlist Rate",v:`${pct(m.shortlisted,Math.max(m.applications,1),1)}%`},
                  {l:"Invite Rate",v:`${pct(m.invitations,Math.max(m.applications,1),1)}%`},
                  {l:"Acceptance",v:`${pct(m.accepted,Math.max(m.invitations,1),1)}%`},
                  {l:"Conversion",v:`${m.conversion||0}%`},
                ].map(r=>(
                  <div key={r.l}>
                    <div style={{ fontSize:12, color:"rgba(255,255,255,.5)", fontWeight:500, textTransform:"uppercase", letterSpacing:0.3 }}>{r.l}</div>
                    <div style={{ fontSize:17, fontWeight:700, color:"#fff", lineHeight:1.2 }}>{r.v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Panel>
      </div>
      {/* Row 3: Health cards + Marketplace scores */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
        <Panel>
          <PanelHeader title="Platform Health" subtitle="Operational monitoring — live" icon={Activity}/>
          <div style={{ padding:"14px 18px", display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10 }}>
            {HEALTH_CARDS.map((h)=>{
              const Icon=h.icon;
              const rC={low:T.success,medium:T.warn,high:T.danger,critical:T.rose}[h.risk];
              return (
                <div key={h.label} style={{ padding:"12px 14px", borderRadius:8, border:`1px solid ${rC}25`, background:`${rC}08` }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                    <Icon size={13} color={rC}/>
                    <Tag label={h.risk} color={rC}/>
                  </div>
                  <div style={{ fontSize:24, fontWeight:700, color:T.text, letterSpacing:-0.8 }}>{h.value}</div>
                  <div style={{ fontSize:13, fontWeight:500, color:T.muted, lineHeight:1.3, marginTop:2 }}>{h.label}</div>
                </div>
              );
            })}
          </div>
        </Panel>
        <Panel>
          <PanelHeader title="Marketplace Health Score" subtitle="KPI gauges across dimensions" icon={Target}/>
          <div style={{ padding:"14px 18px", display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            {MARKETPLACE_SCORES.map((m)=>(
              <div key={m.name} style={{ display:"flex", alignItems:"center", gap:10 }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                    <span style={{ fontSize:14, fontWeight:500, color:T.textMid, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{m.name}</span>
                    <span style={{ fontSize:14, fontWeight:700, color:m.color }}>{m.value}%</span>
                  </div>
                  <ProgressBar value={m.value} color={m.color} height={5}/>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </div>
      {/* Row 4: Executive Insights */}
      <Panel>
        <PanelHeader title="Executive Insights" subtitle="AI-generated operational intelligence" icon={Lightbulb}/>
        <div style={{ padding:"14px 18px", display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12 }}>
          {INSIGHTS.map((ins)=>{
            const Icon=ins.icon;
            return (
              <div key={ins.title} style={{ padding:"12px 14px", borderRadius:8, border:`1px solid ${ins.color}20`, background:`${ins.color}05` }}>
                <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:7 }}>
                  <div style={{ width:24, height:24, borderRadius:6, background:`${ins.color}18`, display:"flex", alignItems:"center", justifyContent:"center" }}><Icon size={12} color={ins.color}/></div>
                  <span style={{ fontSize:14, fontWeight:600, color:T.text }}>{ins.title}</span>
                </div>
                <div style={{ fontSize:14, color:T.muted, lineHeight:1.5 }}>{ins.body}</div>
              </div>
            );
          })}
        </div>
      </Panel>
    </div>
  );
};

// ── Operations Section ────────────────────────────────────────────────────────
const OperationsSection = () => (
  <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
    {/* Kanban + Upcoming side by side */}
    <div style={{ display:"grid", gridTemplateColumns:"1fr 280px", gap:14 }}>
      <Panel style={{ overflow:"visible" }}>
        <PanelHeader title="Event Pipeline" subtitle="Kanban view of event lifecycle" icon={Layers}/>
        <div style={{ padding:"14px 18px", overflowX:"auto" }}>
          <div style={{ display:"flex", gap:12, minWidth:760 }}>
            {KANBAN_STAGES.map((stage)=>(
              <div key={stage.key} style={{ flex:1, minWidth:0 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10, padding:"0 2px" }}>
                  <span style={{ fontSize:13, fontWeight:600, color:stage.color, letterSpacing:0.3, textTransform:"uppercase" }}>{stage.label}</span>
                  <span style={{ background:stage.color+"18", color:stage.color, fontSize:13, fontWeight:700, padding:"1px 7px", borderRadius:99 }}>{stage.count}</span>
                </div>
                <div style={{ display:"flex", flexDirection:"column", gap:7 }}>
                  {stage.cards.map((c,ci)=>(
                    <div key={ci} style={{ padding:"9px 11px", borderRadius:7, border:`1px solid ${stage.color}20`, background:`${stage.color}05`, fontSize:14, fontWeight:500, color:T.text, lineHeight:1.35, cursor:"default" }}>
                      <div style={{ width:20, height:3, borderRadius:2, background:stage.color, marginBottom:6, opacity:0.6 }}/>
                      {c.title}
                      {c.apps > 0 && <div style={{ fontSize:12, color:T.muted, marginTop:4, fontWeight:500 }}>{c.apps} app{c.apps!==1?"s":""}</div>}
                    </div>
                  ))}
                  <div style={{ padding:"6px 10px", borderRadius:7, border:`1.5px dashed ${T.border}`, display:"flex", alignItems:"center", justifyContent:"center", gap:4, color:T.muted, fontSize:13, cursor:"pointer" }}>
                    <Plus size={9}/> Add
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Panel>
      {/* Upcoming */}
      <Panel>
        <PanelHeader title="Upcoming" subtitle="Next 30 days" icon={Calendar}/>
        <div style={{ padding:"10px 14px" }}>
          {UPCOMING.map((u,i)=>(
            <div key={i} style={{ display:"flex", gap:10, alignItems:"flex-start", padding:"8px 0", borderBottom:i<UPCOMING.length-1?`1px solid ${T.borderLight}`:"none" }}>
              <div style={{ width:8, height:8, borderRadius:"50%", background:u.color, marginTop:4, flexShrink:0 }}/>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:14, fontWeight:500, color:T.text, lineHeight:1.3, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{u.label}</div>
                <div style={{ display:"flex", alignItems:"center", gap:5, marginTop:2 }}>
                  <Tag label={u.type} color={u.color}/>
                  <span style={{ fontSize:13, color:T.muted }}>{u.date}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Panel>
    </div>
    {/* SLA Dashboard */}
    <Panel>
      <PanelHeader title="SLA Dashboard" subtitle="Response time and resolution metrics vs targets" icon={Clock}/>
      <div style={{ padding:"14px 18px", display:"flex", flexDirection:"column", gap:10 }}>
        {SLA_METRICS.map((m)=>{
          const ex = m.value > m.target;
          return (
            <div key={m.label} style={{ display:"grid", gridTemplateColumns:"220px 1fr 120px 80px", gap:16, alignItems:"center", padding:"10px 14px", borderRadius:8, background:ex?`${T.warn}06`:`${T.success}06`, border:`1px solid ${ex?T.warn:T.success}18` }}>
              <span style={{ fontSize:14, fontWeight:500, color:T.text }}>{m.label}</span>
              <ProgressBar value={m.value} max={Math.max(m.target*1.5,m.value)} color={ex?T.warn:T.success} height={6}/>
              <div style={{ textAlign:"right" }}>
                <span style={{ fontSize:15, fontWeight:700, color:ex?T.warn:T.success }}>{m.value} {m.unit}</span>
                <div style={{ fontSize:12, color:T.muted }}>Target: {m.target} {m.unit}</div>
              </div>
              <div style={{ textAlign:"right" }}><Tag label={ex?"At Risk":"On Track"} color={ex?T.warn:T.success}/></div>
            </div>
          );
        })}
      </div>
    </Panel>
    {/* Leaderboards side by side */}
    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
      <LeaderboardMini title="Top Schools" icon={Building2} data={TOP_SCHOOLS.slice(0,8)} columns={["School","Apps","Score"]} rowFn={r=>[`${r.flag} ${r.school}`,r.apps,r.score]}/>
      <LeaderboardMini title="Top Universities" icon={GraduationCap} data={TOP_UNIVERSITIES.slice(0,8)} columns={["University","Apps","Eng."]} rowFn={r=>[`${r.flag} ${r.university}`,r.apps,r.engagement]}/>
    </div>
  </div>
);

// ── Compact Leaderboard ───────────────────────────────────────────────────────
const LeaderboardMini = ({ title, icon, data, columns, rowFn }) => {
  const gold = {1:"#F59E0B",2:"#94A3B8",3:"#CD7F32"};
  return (
    <Panel>
      <PanelHeader title={title} icon={icon}/>
      <div style={{ overflowX:"auto" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:14 }}>
          <thead>
            <tr style={{ background:T.bg }}>
              <th style={{ padding:"8px 14px", textAlign:"left", fontSize:12, fontWeight:600, color:T.muted, textTransform:"uppercase", letterSpacing:0.4, width:28 }}>#</th>
              {columns.map(c=><th key={c} style={{ padding:"8px 12px", textAlign:"left", fontSize:12, fontWeight:600, color:T.muted, textTransform:"uppercase", letterSpacing:0.4, whiteSpace:"nowrap" }}>{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.map((row,i)=>{
              const cells = rowFn(row);
              return (
                <tr key={i} style={{ borderTop:`1px solid ${T.borderLight}` }}>
                  <td style={{ padding:"8px 14px" }}>
                    <span style={{ fontSize:13, fontWeight:700, color:gold[row.rank]||T.muted }}>{row.rank}</span>
                  </td>
                  {cells.map((c,ci)=>(
                    <td key={ci} style={{ padding:"8px 12px", color:ci===0?T.navy:T.textMid, fontWeight:ci===0?500:400, whiteSpace:"nowrap", overflow:ci===0?"hidden":"visible", textOverflow:ci===0?"ellipsis":"clip", maxWidth:ci===0?180:"auto" }}>
                      {ci===cells.length-1
                        ? <span style={{ background:T.navy, color:"#fff", padding:"1px 7px", borderRadius:4, fontSize:13, fontWeight:600 }}>{c}</span>
                        : c}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Panel>
  );
};

// ── Events Section ────────────────────────────────────────────────────────────
const EventsSection = () => {
  const [search,setSearch]=useState("");
  const [statusFilter,setStatusFilter]=useState("");
  const [page,setPage]=useState(0);
  const PAGE_SIZE=10;
  const filtered=EVENT_PERFORMANCE.filter(e=>(!search||e.event.toLowerCase().includes(search.toLowerCase())||e.country.toLowerCase().includes(search.toLowerCase()))&&(!statusFilter||e.status===statusFilter));
  const paged=filtered.slice(page*PAGE_SIZE,(page+1)*PAGE_SIZE);
  const totalPages=Math.ceil(filtered.length/PAGE_SIZE);

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
      {/* Summary row */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:10 }}>
        {[
          {label:"Total Events",value:EVENT_PERFORMANCE.length,color:T.accent,icon:CalendarDays},
          {label:"Active",value:EVENT_PERFORMANCE.filter(e=>e.status==="registration_open"||e.status==="published").length,color:T.success,icon:Activity},
          {label:"Total Applications",value:EVENT_PERFORMANCE.reduce((s,e)=>s+e.apps,0),color:T.purple,icon:FileText},
          {label:"Total Invites",value:EVENT_PERFORMANCE.reduce((s,e)=>s+e.invited,0),color:T.cyan,icon:Send},
          {label:"Total Accepted",value:EVENT_PERFORMANCE.reduce((s,e)=>s+e.accepted,0),color:T.teal,icon:CheckCircle},
        ].map(k=>{const Icon=k.icon;return(
          <Panel key={k.label} style={{ padding:"14px 16px" }}>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <div style={{ width:30,height:30,borderRadius:8,background:k.color+"15",display:"flex",alignItems:"center",justifyContent:"center" }}><Icon size={14} color={k.color}/></div>
              <div><div style={{ fontSize:19,fontWeight:700,color:T.text,letterSpacing:-0.5 }}>{k.value.toLocaleString()}</div><div style={{ fontSize:13,color:T.muted,fontWeight:500 }}>{k.label}</div></div>
            </div>
          </Panel>
        );})}
      </div>
      {/* Pie charts row */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14 }}>
        {[
          {title:"Apps by Region",data:PIE_APPS_REGION},
          {title:"Invites by Region",data:PIE_INVITES_REGION},
          {title:"Accepted by Region",data:PIE_ACCEPTED_REGION},
          {title:"Events by Category",data:PIE_EVENTS_CATEGORY},
        ].map(c=>(
          <Panel key={c.title}>
            <PanelHeader title={c.title} compact/>
            <div style={{ padding:"10px 14px 14px" }}>
              <ResponsiveContainer width="100%" height={130}>
                <PieChart>
                  <Pie data={c.data} cx="50%" cy="50%" innerRadius={38} outerRadius={58} dataKey="value" paddingAngle={2}>
                    {c.data.map((e,i)=><Cell key={i} fill={e.fill}/>)}
                  </Pie>
                  <Tooltip contentStyle={{borderRadius:7,border:`1px solid ${T.border}`,fontSize:13}}/>
                </PieChart>
              </ResponsiveContainer>
              <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
                {c.data.slice(0,3).map((d)=>(
                  <div key={d.name} style={{ display:"flex", alignItems:"center", gap:5 }}>
                    <span style={{ width:6,height:6,borderRadius:"50%",background:d.fill,flexShrink:0 }}/>
                    <span style={{ fontSize:13,color:T.muted,flex:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis" }}>{d.name}</span>
                    <span style={{ fontSize:13,fontWeight:600,color:T.text }}>{d.value.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          </Panel>
        ))}
      </div>
      {/* Events Table */}
      <Panel>
        <PanelHeader title="Event Performance" subtitle={`${filtered.length} events`} icon={BarChart2}
          right={
            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
              <div style={{ position:"relative" }}>
                <Search size={11} style={{ position:"absolute",left:8,top:"50%",transform:"translateY(-50%)",color:T.muted }}/>
                <input value={search} onChange={e=>{setSearch(e.target.value);setPage(0);}} placeholder="Search events…"
                  style={{ paddingLeft:26,paddingRight:10,paddingTop:5,paddingBottom:5,border:`1px solid ${T.border}`,borderRadius:7,fontSize:14,outline:"none",width:160,color:T.text }}/>
              </div>
              <select value={statusFilter} onChange={e=>{setStatusFilter(e.target.value);setPage(0);}}
                style={{ padding:"5px 10px",border:`1px solid ${T.border}`,borderRadius:7,fontSize:14,outline:"none",color:T.text }}>
                <option value="">All</option>
                {["draft","under_review","published","registration_open","invitations_released","completed"].map(s=><option key={s} value={s}>{s}</option>)}
              </select>
              <Btn icon={Download} variant="ghost">CSV</Btn>
            </div>
          }
        />
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%",borderCollapse:"collapse",fontSize:14 }}>
            <thead>
              <tr style={{ background:T.bg }}>
                {["Event","Country","Apps","Shortlisted","Invited","Accepted","Status"].map(h=>(
                  <th key={h} style={{ padding:"9px 14px",textAlign:"left",fontSize:13,fontWeight:600,color:T.muted,textTransform:"uppercase",letterSpacing:0.4,whiteSpace:"nowrap",borderBottom:`1px solid ${T.border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paged.map((e,i)=>(
                <tr key={i} style={{ borderBottom:`1px solid ${T.borderLight}`, background:i%2===0?"#fff":T.bg+"99" }}>
                  <td style={{ padding:"10px 14px",fontWeight:500,color:T.navy,maxWidth:240,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis" }}>{e.event}</td>
                  <td style={{ padding:"10px 14px",color:T.muted,fontSize:14 }}>{e.country}</td>
                  <td style={{ padding:"10px 14px",fontWeight:600,color:T.accent }}>{e.apps.toLocaleString()}</td>
                  <td style={{ padding:"10px 14px",fontWeight:600,color:T.indigo }}>{e.shortlisted.toLocaleString()}</td>
                  <td style={{ padding:"10px 14px",fontWeight:600,color:T.purple }}>{e.invited.toLocaleString()}</td>
                  <td style={{ padding:"10px 14px",fontWeight:600,color:T.teal }}>{e.accepted.toLocaleString()}</td>
                  <td style={{ padding:"10px 14px" }}><StatusBadge status={e.status}/></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ padding:"10px 18px",display:"flex",justifyContent:"space-between",alignItems:"center",borderTop:`1px solid ${T.borderLight}` }}>
          <span style={{ fontSize:14,color:T.muted }}>{filtered.length} events · Page {page+1} of {Math.max(totalPages,1)}</span>
          <div style={{ display:"flex",gap:4 }}>
            {Array.from({length:totalPages}).slice(0,8).map((_,p)=>(
              <button key={p} onClick={()=>setPage(p)} style={{ width:26,height:26,borderRadius:5,border:`1px solid ${page===p?T.navy:T.border}`,background:page===p?T.navy:"transparent",color:page===p?"#fff":T.muted,fontSize:13,fontWeight:600,cursor:"pointer"}}>{p+1}</button>
            ))}
          </div>
        </div>
      </Panel>
    </div>
  );
};

// ── Global Section ────────────────────────────────────────────────────────────
const GlobalSection = () => {
  const [metric,setMetric]=useState("apps");
  const rColors={"North America":T.accent,"Europe":T.indigo,"Asia Pacific":T.teal,"Middle East":T.orange,"Africa":T.purple,"South America":T.cyan};
  const max=REGION_DATA.length?Math.max(...REGION_DATA.map(r=>r[metric])):1;
  const gold={1:"#F59E0B",2:"#94A3B8",3:"#CD7F32"};
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
        {/* Regional bars */}
        <Panel>
          <PanelHeader title="Regional Distribution" subtitle="Platform activity by geography" icon={Globe}
            right={
              <div style={{ display:"flex",gap:5 }}>
                {[["apps","Apps"],["events","Events"],["schools","Schools"],["univs","Univs"]].map(([k,l])=>(
                  <button key={k} onClick={()=>setMetric(k)} style={{ padding:"3px 9px",borderRadius:5,border:`1px solid ${metric===k?T.navy:T.border}`,background:metric===k?T.navy:"transparent",color:metric===k?"#fff":T.muted,fontSize:13,fontWeight:600,cursor:"pointer"}}>{l}</button>
                ))}
              </div>
            }
          />
          <div style={{ padding:"14px 18px" }}>
            {REGION_DATA.map((r)=>{
              const v=r[metric],bp=max?(v/max)*100:0,c=rColors[r.region]||T.accent;
              return (
                <div key={r.region} style={{ marginBottom:12 }}>
                  <div style={{ display:"flex",justifyContent:"space-between",marginBottom:4 }}>
                    <span style={{ fontSize:14,fontWeight:500,color:T.text }}>{r.region}</span>
                    <span style={{ fontSize:14,fontWeight:700,color:c }}>{v.toLocaleString()}</span>
                  </div>
                  <ProgressBar value={bp} max={100} color={c} height={7}/>
                </div>
              );
            })}
          </div>
        </Panel>
        {/* Country leaderboard */}
        <Panel>
          <PanelHeader title="Top Countries" subtitle="Ranked by total activity" icon={Globe}/>
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%",borderCollapse:"collapse",fontSize:14 }}>
              <thead>
                <tr style={{ background:T.bg }}>
                  {["#","Country","Schools","Apps","Invites","Accepted","Share"].map(h=>(
                    <th key={h} style={{ padding:"8px 12px",textAlign:"left",fontSize:12,fontWeight:600,color:T.muted,textTransform:"uppercase",letterSpacing:0.4,borderBottom:`1px solid ${T.border}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {TOP_COUNTRIES.map((c,i)=>(
                  <tr key={c.country} style={{ borderBottom:`1px solid ${T.borderLight}` }}>
                    <td style={{ padding:"8px 12px" }}><span style={{ fontSize:13,fontWeight:700,color:gold[i+1]||T.muted }}>{i+1}</span></td>
                    <td style={{ padding:"8px 12px" }}><span style={{ fontSize:14,fontWeight:500,color:T.text }}>{c.flag} {c.country}</span></td>
                    <td style={{ padding:"8px 12px",color:T.accent,fontWeight:600 }}>{c.schools}</td>
                    <td style={{ padding:"8px 12px",color:T.purple,fontWeight:600 }}>{c.applications}</td>
                    <td style={{ padding:"8px 12px",color:T.cyan,fontWeight:600 }}>{c.invitations}</td>
                    <td style={{ padding:"8px 12px",color:T.success,fontWeight:600 }}>{c.accepted}</td>
                    <td style={{ padding:"8px 12px" }}><Tag label={c.share} color={T.accent}/></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>
      {/* Activity area chart */}
      <Panel>
        <PanelHeader title="Global Activity Timeline" subtitle="Monthly volume across all activity types" icon={BarChart2}/>
        <div style={{ padding:"16px 18px" }}>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={REVENUE_DATA} margin={{top:4,right:4,left:-20,bottom:0}}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.borderLight}/>
              <XAxis dataKey="month" tick={{fontSize:12,fill:T.muted}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fontSize:12,fill:T.muted}} axisLine={false} tickLine={false}/>
              <Tooltip contentStyle={{borderRadius:8,border:`1px solid ${T.border}`,fontSize:14}}/>
              <Legend wrapperStyle={{fontSize:13,paddingTop:8}}/>
              <Bar dataKey="schools" fill={T.accent} stackId="a" radius={[0,0,0,0]}/>
              <Bar dataKey="universities" fill={T.teal} stackId="a"/>
              <Bar dataKey="events" fill={T.orange} stackId="a"/>
              <Bar dataKey="applications" fill={T.purple} stackId="a" radius={[3,3,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Panel>
    </div>
  );
};

// ── AI & Insights Section ─────────────────────────────────────────────────────
const InsightsSection = () => (
  <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
    <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:14 }}>
      {AI_METRICS.map((m)=>{
        const Icon=m.icon;
        return (
          <Panel key={m.label} style={{ padding:"16px 18px" }}>
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12 }}>
              <div style={{ width:32,height:32,borderRadius:8,background:m.color+"15",display:"flex",alignItems:"center",justifyContent:"center" }}><Icon size={14} color={m.color}/></div>
              <Tag label={m.trend} color={T.success}/>
            </div>
            <div style={{ fontSize:24,fontWeight:700,color:m.color,letterSpacing:-0.5,marginBottom:2 }}>{m.value}</div>
            <div style={{ fontSize:14,fontWeight:600,color:T.text,marginBottom:2 }}>{m.label}</div>
            <div style={{ fontSize:14,color:T.muted }}>{m.sub}</div>
          </Panel>
        );
      })}
    </div>
    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
      <Panel>
        <PanelHeader title="Platform Growth" subtitle="Schools, Universities, Events, Applications" icon={TrendingUp}/>
        <div style={{ padding:"16px 18px" }}>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={GROWTH_DATA} margin={{top:4,right:4,left:-20,bottom:0}}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.borderLight}/>
              <XAxis dataKey="month" tick={{fontSize:12,fill:T.muted}} interval={2} axisLine={false} tickLine={false}/>
              <YAxis tick={{fontSize:12,fill:T.muted}} axisLine={false} tickLine={false}/>
              <Tooltip contentStyle={{borderRadius:8,border:`1px solid ${T.border}`,fontSize:14}}/>
              <Legend wrapperStyle={{fontSize:13}}/>
              <Area type="monotone" dataKey="Invitations" stroke={T.cyan} fill={T.cyan+"15"} strokeWidth={1.5} dot={false}/>
              <Area type="monotone" dataKey="Accepted" stroke={T.success} fill={T.success+"15"} strokeWidth={1.5} dot={false}/>
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Panel>
      <Panel>
        <PanelHeader title="Activity by Region" subtitle="Distribution of total platform activity" icon={PieIcon}/>
        <div style={{ padding:"16px 18px", display:"flex", gap:20, alignItems:"center" }}>
          <ResponsiveContainer width={160} height={160}>
            <PieChart>
              <Pie data={REVENUE_BY_REGION} cx="50%" cy="50%" innerRadius={48} outerRadius={72} dataKey="value" paddingAngle={2}>
                {REVENUE_BY_REGION.map((e,i)=><Cell key={i} fill={e.fill}/>)}
              </Pie>
              <Tooltip contentStyle={{borderRadius:7,border:`1px solid ${T.border}`,fontSize:13}}/>
            </PieChart>
          </ResponsiveContainer>
          <div style={{ flex:1, display:"flex", flexDirection:"column", gap:7 }}>
            {REVENUE_BY_REGION.map((d)=>(
              <div key={d.name} style={{ display:"flex", alignItems:"center", gap:7 }}>
                <span style={{ width:8,height:8,borderRadius:2,background:d.fill,flexShrink:0 }}/>
                <span style={{ fontSize:14,color:T.textMid,flex:1 }}>{d.name}</span>
                <span style={{ fontSize:14,fontWeight:700,color:T.text }}>{d.value.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      </Panel>
    </div>
  </div>
);

// ── Compliance Section ────────────────────────────────────────────────────────
const ComplianceSection = () => (
  <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
    <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12 }}>
      {RISK_DATA.map((r)=>{
        const sm={low:{bg:"#dcfce7",border:"#bbf7d0",text:T.success},medium:{bg:"#fef3c7",border:"#fde68a",text:T.warn},high:{bg:"#fee2e2",border:"#fecaca",text:T.danger},critical:{bg:"#ffe4e6",border:"#fecdd3",text:T.rose}}[r.severity];
        return (
          <div key={r.label} style={{ padding:"16px 18px",borderRadius:10,background:sm.bg,border:`1px solid ${sm.border}` }}>
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10 }}>
              <StatusBadge status={r.severity}/>
            </div>
            <div style={{ fontSize:30,fontWeight:700,color:sm.text,letterSpacing:-1 }}>{r.value.toLocaleString()}</div>
            <div style={{ fontSize:14,fontWeight:500,color:T.text,lineHeight:1.3,marginTop:4 }}>{r.label}</div>
          </div>
        );
      })}
    </div>
    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
      <Panel>
        <PanelHeader title="SLA Performance" subtitle="vs operational targets" icon={Clock}/>
        <div style={{ padding:"14px 18px", display:"flex", flexDirection:"column", gap:12 }}>
          {SLA_METRICS.map((m)=>{
            const ex=m.value>m.target;
            return (
              <div key={m.label}>
                <div style={{ display:"flex",justifyContent:"space-between",marginBottom:5 }}>
                  <span style={{ fontSize:14,fontWeight:500,color:T.text }}>{m.label}</span>
                  <div style={{ display:"flex",gap:8,alignItems:"center" }}>
                    <span style={{ fontSize:14,fontWeight:700,color:ex?T.warn:T.success }}>{m.value} {m.unit}</span>
                    <Tag label={ex?"Risk":"OK"} color={ex?T.warn:T.success}/>
                  </div>
                </div>
                <ProgressBar value={m.value} max={Math.max(m.target*1.5,m.value)} color={ex?T.warn:T.success} height={6}/>
                <div style={{ fontSize:12,color:T.muted,marginTop:2 }}>Target: {m.target} {m.unit}</div>
              </div>
            );
          })}
        </div>
      </Panel>
      <Panel>
        <PanelHeader title="Platform Health" subtitle="Operational monitoring metrics" icon={Activity}/>
        <div style={{ padding:"14px 18px", display:"flex", flexDirection:"column", gap:10 }}>
          {HEALTH_CARDS.map((h)=>{
            const Icon=h.icon;
            const rC={low:T.success,medium:T.warn,high:T.danger,critical:T.rose}[h.risk];
            return (
              <div key={h.label} style={{ display:"flex",alignItems:"center",gap:12,padding:"10px 12px",borderRadius:8,border:`1px solid ${rC}20`,background:`${rC}06` }}>
                <Icon size={14} color={rC}/>
                <span style={{ flex:1,fontSize:14,fontWeight:500,color:T.text }}>{h.label}</span>
                <span style={{ fontSize:17,fontWeight:700,color:rC }}>{h.value}</span>
                <Tag label={h.risk} color={rC}/>
              </div>
            );
          })}
        </div>
      </Panel>
    </div>
  </div>
);

// ── Right Sidebar (notifications + activity) ──────────────────────────────────
const RightPanel = () => {
  const nC={info:T.accent,warn:T.warn,success:T.success,danger:T.danger};
  return (
    <div style={{ width:260, flexShrink:0, display:"flex", flexDirection:"column", gap:14, height:"100%", overflowY:"auto" }}>
      <Panel>
        <PanelHeader title="Notifications" icon={Bell} compact
          right={<span style={{ background:T.danger,color:"#fff",borderRadius:99,fontSize:12,fontWeight:700,padding:"1px 6px" }}>{NOTIFICATIONS.length}</span>}
        />
        <div style={{ padding:"8px 0" }}>
          {NOTIFICATIONS.map((n,i)=>(
            <div key={i} style={{ display:"flex",gap:9,padding:"8px 14px",borderBottom:i<NOTIFICATIONS.length-1?`1px solid ${T.borderLight}`:"none" }}>
              <div style={{ width:6,height:6,borderRadius:"50%",background:nC[n.type]||T.muted,marginTop:5,flexShrink:0 }}/>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:14,color:T.text,lineHeight:1.4,fontWeight:500 }}>{n.text}</div>
                <div style={{ fontSize:12,color:T.muted,marginTop:2 }}>{n.time}</div>
              </div>
            </div>
          ))}
        </div>
      </Panel>
      <Panel style={{ flex:1 }}>
        <PanelHeader title="Recent Activity" icon={Activity} compact/>
        <div style={{ padding:"8px 0", maxHeight:460, overflowY:"auto" }}>
          {ACTIVITY_FEED.map((a,i)=>{
            const statusColor={applied:T.accent,shortlisted:T.indigo,invited:T.purple,accepted:T.success,rejected:T.danger,pending:T.warn,published:T.teal,completed:T.success,draft:T.muted}[a.status]||T.muted;
            return (
              <div key={i} style={{ display:"flex",gap:9,padding:"8px 14px",borderBottom:i<ACTIVITY_FEED.length-1?`1px solid ${T.borderLight}`:"none" }}>
                <div style={{ width:6,height:6,borderRadius:2,background:statusColor,marginTop:5,flexShrink:0 }}/>
                <div style={{ flex:1,minWidth:0 }}>
                  <div style={{ fontSize:13,fontWeight:600,color:T.navy }}>{a.action}</div>
                  <div style={{ fontSize:13,color:T.textMid,lineHeight:1.4,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis" }}>{a.detail}</div>
                  {a.sub&&<div style={{ fontSize:12,color:T.muted,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis" }}>{a.sub}</div>}
                  <div style={{ fontSize:12,color:T.border,marginTop:1 }}>{a.time} ago</div>
                </div>
              </div>
            );
          })}
        </div>
      </Panel>
    </div>
  );
};

// ── Top Nav Bar (horizontal, like original) ───────────────────────────────────
const NAV_SECTIONS = [
  { key:"overview",   label:"Overview",   icon:LayoutDashboard },
  { key:"operations", label:"Operations", icon:Activity        },
  { key:"events",     label:"Events",     icon:CalendarDays    },
  { key:"global",     label:"Global",     icon:Globe           },
  { key:"insights",   label:"AI Insights",icon:Brain           },
  { key:"compliance", label:"Compliance", icon:Shield          },
];

const TopNav = ({ activeSection, onChange, lastUpdate, refreshing, onRefresh }) => (
  <div style={{ background:`linear-gradient(90deg,${T.navyDark} 0%,${T.navyMid} 100%)`, padding:"0 24px", display:"flex", alignItems:"center", justifyContent:"space-between", height:52, position:"sticky", top:0, zIndex:100, boxShadow:"0 2px 16px rgba(10,35,82,.25)", flexShrink:0 }}>
    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
      <div style={{ display:"flex", alignItems:"center", gap:9, marginRight:10 }}>
        <div style={{ width:30, height:30, borderRadius:8, background:T.orange, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <Globe size={15} color="#fff"/>
        </div>
        <div>
          <div style={{ fontSize:14, fontWeight:700, color:"#fff", letterSpacing:-0.3, lineHeight:1.1 }}>EduPlatform</div>
          <div style={{ fontSize:12, color:"rgba(255,255,255,.45)", letterSpacing:0.4, textTransform:"uppercase", lineHeight:1 }}>Global Ops</div>
        </div>
      </div>
      <div style={{ width:1, height:22, background:"rgba(255,255,255,.15)", margin:"0 6px" }}/>
      {NAV_SECTIONS.map((s) => {
        const Icon = s.icon;
        const isActive = activeSection === s.key;
        return (
          <button key={s.key} onClick={() => onChange(s.key)}
            style={{ display:"flex", alignItems:"center", gap:6, padding:"5px 13px", borderRadius:7, border:"none", background:isActive?"rgba(255,255,255,.13)":"transparent", color:isActive?"#fff":"rgba(255,255,255,.5)", fontSize:14, fontWeight:isActive?600:400, cursor:"pointer", transition:"all .15s" }}>
            <Icon size={14}/>{s.label}
          </button>
        );
      })}
    </div>
    <div style={{ display:"flex", alignItems:"center", gap:12 }}>
      <div style={{ display:"flex", alignItems:"center", gap:5 }}>
        <div style={{ width:6, height:6, borderRadius:"50%", background:T.success }}/>
        <span style={{ fontSize:13, color:"rgba(255,255,255,.5)" }}>Live · {lastUpdate.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}</span>
      </div>
      <button onClick={onRefresh}
        style={{ display:"flex", alignItems:"center", gap:5, padding:"5px 12px", background:"rgba(255,255,255,.1)", border:"1px solid rgba(255,255,255,.18)", borderRadius:7, color:"#fff", fontSize:13, fontWeight:500, cursor:"pointer" }}>
        <RefreshCw size={12} style={{ animation:refreshing?"spin 1s linear infinite":"none" }}/>
        Refresh
      </button>
      <div style={{ width:30, height:30, borderRadius:"50%", background:T.orange, display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:700, fontSize:14, cursor:"pointer" }}>PM</div>
    </div>
  </div>
);



// ── Root ──────────────────────────────────────────────────────────────────────
function GlobalPlatformDashboard() {
  const [activeSection, setActiveSection] = useState("overview");
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [,forceUpdate] = useState(0);

  const loadDashboardData = async () => {
    setRefreshing(true); setLoading(true);
    try {
      const uc = getUserCredentials();
      const tid = getTenantId(uc);
      const creds = Fetchexecutequery();

      const [schoolRes,universityRes,eventRes,applicationRes,invitationRes] = await Promise.all([
        axios.post(creds[1],{query:`SELECT primary_id,school_name,city,state,country,profile_status,completion_percentage,entity_id,user_id,created_date,modified_date FROM edu_platform.school_profile WHERE tenant_id=${tid} AND is_active=1 ORDER BY modified_date DESC`},creds[0]),
        axios.post(creds[1],{query:`SELECT primary_id,university_name,city,state,country,profile_status,completion_percentage,entity_id,user_id,created_date,modified_date FROM edu_platform.university_profile WHERE tenant_id=${tid} AND is_active=1 ORDER BY modified_date DESC`},creds[0]),
        axios.post(creds[1],{query:`SELECT primary_id,event_name,event_category,country,city,event_status,event_from_date,event_to_date,reg_close_date,acceptance_deadline,creator_entity_id,created_date,modified_date FROM edu_platform.events WHERE tenant_id=${tid} AND is_active=1 ORDER BY modified_date DESC`},creds[0]),
        axios.post(creds[1],{query:`SELECT ea.primary_id,ea.event_id,ea.applicant_entity_id,ea.application_status,ea.applied_on,ea.reviewed_on,ea.invited_on,ea.response_deadline,ea.responded_on,ea.review_status,ea.invitation_status,ea.modified_date,e.event_name,e.country,e.city,e.event_from_date,e.event_to_date FROM edu_platform.event_applications ea JOIN edu_platform.events e ON e.primary_id=ea.event_id WHERE ea.tenant_id=${tid} AND ea.is_active=1 ORDER BY ea.modified_date DESC`},creds[0]),
        axios.post(creds[1],{query:`SELECT ei.primary_id,ei.event_id,ei.application_id,ei.applicant_entity_id,ei.invitation_status,ei.invited_on,ei.acceptance_deadline,ei.responded_on,ei.modified_date,e.event_name,e.country,e.city,e.event_from_date,e.event_to_date FROM edu_platform.event_invitations ei JOIN edu_platform.events e ON e.primary_id=ei.event_id WHERE ei.tenant_id=${tid} AND ei.is_active=1 ORDER BY ei.modified_date DESC`},creds[0]),
      ]);

      applyGlobalDashboardData(buildGlobalDashboardData({
        schools:     schoolRes.data     || [],
        universities:universityRes.data || [],
        events:      eventRes.data      || [],
        applications:applicationRes.data|| [],
        invitations: invitationRes.data || [],
      }));
      setLastUpdate(new Date()); forceUpdate(n=>n+1);
    } catch(err) {
      console.error("Dashboard load failed:", err);
    } finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { loadDashboardData(); }, []);

  const SECTION_MAP = { overview:<OverviewSection/>, operations:<OperationsSection/>, events:<EventsSection/>, global:<GlobalSection/>, insights:<InsightsSection/>, compliance:<ComplianceSection/> };

  return (
    <div style={{ display:"flex", flexDirection:"column", overflow:"hidden", background:T.bg }}>
      <TopNav
        activeSection={activeSection}
        onChange={setActiveSection}
        lastUpdate={lastUpdate}
        refreshing={refreshing}
        onRefresh={loadDashboardData}
      />
      {/* Content row: main + right panel */}
      <div style={{ flex:1, display:"flex", overflow:"hidden", opacity:loading?0.7:1, transition:"opacity .2s" }}>
        <div style={{ flex:1, overflowY:"auto", padding:18, minWidth:0 }}>
          {SECTION_MAP[activeSection]}
        </div>
        <div style={{ width:264, flexShrink:0, borderLeft:`1px solid ${T.border}`, background:"#fff", overflowY:"auto", padding:14 }}>
          <RightPanel/>
        </div>
      </div>
      <style>{`
        @keyframes spin { from{transform:rotate(0deg)}to{transform:rotate(360deg)} }
        *{box-sizing:border-box;margin:0;padding:0;}
        ::-webkit-scrollbar{width:4px;height:4px}
        ::-webkit-scrollbar-track{background:transparent}
        ::-webkit-scrollbar-thumb{background:${T.border};border-radius:99px}
        ::-webkit-scrollbar-thumb:hover{background:${T.muted}}
        button:hover{opacity:.85}
      `}
      </style>
    </div>
  );
}

export default GlobalPlatformDashboard;